import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Button,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import Iconify from '../../../components/iconify';
import BreadCrumb from '../../../components/breadcrumb/BreadCrumbNavigation';
import CompOffStatusBadge from './CompOffStatusBadge';
import { COMP_OFF_STATUS } from '../constants';
import { getCompOffById, updateCompOffStatus, cancelCompOffRequest } from '../../../services/compOffService';
import { isAdmin, isManager, isHR, checkComponentPermission } from '../../../utils/share';
import Loading from '../../../layouts/loading';
import { useStyles } from '../styles/CompOffDetails';

const CompOffDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state?.login?.user || null);
  const userId = user?.id;
  const [compOff, setCompOff] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [creditDays, setCreditDays] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const classes = useStyles();

  const isOwnRequest = compOff?.userId === userId;
  const canApproveReject =
    (isAdmin() || ((isManager() || isHR()) && !isOwnRequest)) &&
    checkComponentPermission('APPROVE_COMP_OFF') &&
    compOff?.status === COMP_OFF_STATUS.PENDING;
  const canCancel =
    isOwnRequest && compOff?.status === COMP_OFF_STATUS.PENDING && checkComponentPermission('DELETE_COMP_OFF');

  const fetchDetails = async () => {
    setIsLoading(true);
    try {
      const response = await getCompOffById(id);
      setCompOff(response?.data?.data);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error fetching comp-off details');
      navigate('/comp-off');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd-MMM-yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const calculateCreditDays = (hoursWorked) => {
    const raw = Math.round((parseFloat(hoursWorked) / 8) * 2) / 2;
    return Math.min(raw, 1.0);
  };

  const handleOpenApprove = () => {
    setCreditDays(calculateCreditDays(compOff.hoursWorked));
    setApproveDialogOpen(true);
  };

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      await updateCompOffStatus(id, { status: 'approved', creditDays });
      toast.success('Comp-off request approved successfully');
      setApproveDialogOpen(false);
      fetchDetails();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error approving comp-off request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setIsSubmitting(true);
    try {
      await updateCompOffStatus(id, { status: 'rejected', rejectionReason: rejectionReason.trim() });
      toast.success('Comp-off request rejected successfully');
      setRejectDialogOpen(false);
      setRejectionReason('');
      fetchDetails();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error rejecting comp-off request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    setIsSubmitting(true);
    try {
      await cancelCompOffRequest(id);
      toast.success('Comp-off request deleted successfully');
      setCancelDialogOpen(false);
      navigate('/comp-off');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error deleting comp-off request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Comp-Off', path: '/comp-off' },
    { label: 'Comp-Off Details', path: '' },
  ];

  if (isLoading) {
    return <Loading open={isLoading} />;
  }

  if (!compOff) {
    return null;
  }

  return (
    <>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Box sx={{ maxWidth: 1000, margin: 'auto', padding: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4">Comp-Off Request Details</Typography>
          <Button
            variant="outlined"
            startIcon={<Iconify icon="eva:arrow-back-fill" />}
            onClick={() => navigate('/comp-off')}
          >
            Back to Comp-Off
          </Button>
        </Stack>

        <Card className={classes.compOffDetailsCard}>
          <CardContent>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Comp-Off Information</Typography>
                <CompOffStatusBadge status={compOff.status} />
              </Box>

              <Divider />

              <Grid container spacing={2} gap={1}>
                <Grid item xs={12} md={2.5} className={classes.detailIndCard}>
                  <Typography variant="body2" color="text.secondary">
                    Employee Name
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {compOff.employee?.userName || compOff.employee?.email || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={2.5} className={classes.detailIndCard}>
                  <Typography variant="body2" color="text.secondary">
                    Work Date
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {formatDate(compOff.workDate)}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={2.5} className={classes.detailIndCard}>
                  <Typography variant="body2" color="text.secondary">
                    Hours Worked
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {compOff.hoursWorked}h
                  </Typography>
                </Grid>

                <Grid item xs={12} md={2.5} className={classes.detailIndCard}>
                  <Typography variant="body2" color="text.secondary">
                    Credit Days
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {compOff.creditDays != null ? `${compOff.creditDays} day(s)` : 'Pending approval'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={2.5} className={classes.detailIndCard}>
                  <Typography variant="body2" color="text.secondary">
                    Requested Date
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {formatDate(compOff.createdAt)}
                  </Typography>
                </Grid>

                <Grid item xs={12} className={classes.detailIndCard}>
                  <Typography variant="body2" color="text.secondary">
                    Tasks Completed
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {compOff.tasksCompleted || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} className={classes.detailIndCard}>
                  <Typography variant="body2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {compOff.description || '-'}
                  </Typography>
                </Grid>

                {compOff.reviewers && Array.isArray(compOff.reviewers) && compOff.reviewers.length > 0 && (
                  <Grid item xs={12} className={classes.detailIndCard}>
                    <Typography variant="body2" color="text.secondary">
                      Selected Reviewers
                    </Typography>
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {compOff.reviewerDetails && compOff.reviewerDetails.length > 0
                        ? compOff.reviewerDetails.map((reviewer) => (
                            <Chip
                              key={reviewer.id}
                              label={reviewer.userName || reviewer.email || `User ${reviewer.id}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ))
                        : compOff.reviewers.map((reviewerId) => (
                            <Chip
                              key={reviewerId}
                              label={`User ${reviewerId}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      These reviewers have been notified via email
                    </Typography>
                  </Grid>
                )}

                {compOff.reviewedBy && (
                  <>
                    <Grid item xs={12} md={6} className={classes.detailIndCard}>
                      <Typography variant="body2" color="text.secondary">
                        Reviewed By
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                        {compOff.approver && compOff.approver.id === userId
                          ? 'Me'
                          : compOff.approver?.userName || compOff.approver?.email || '-'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6} className={classes.detailIndCard}>
                      <Typography variant="body2" color="text.secondary">
                        Reviewed Date
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                        {formatDate(compOff.reviewedAt)}
                      </Typography>
                    </Grid>
                  </>
                )}

                {compOff.rejectionReason && (
                  <Grid item xs={12} className={classes.detailIndCard}>
                    <Typography variant="body2" color="text.secondary">
                      Rejection Reason
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, color: 'error.main', whiteSpace: 'pre-wrap' }}>
                      {compOff.rejectionReason}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {(canApproveReject || canCancel) && (
                <>
                  <Divider />
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    {canCancel && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Iconify icon="material-symbols:delete" />}
                        onClick={() => setCancelDialogOpen(true)}
                      >
                        Delete Request
                      </Button>
                    )}
                    {canApproveReject && (
                      <>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<Iconify icon="eva:close-circle-fill" />}
                          onClick={() => setRejectDialogOpen(true)}
                        >
                          Reject
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<Iconify icon="eva:checkmark-circle-2-fill" />}
                          onClick={handleOpenApprove}
                        >
                          Approve
                        </Button>
                      </>
                    )}
                  </Stack>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Approve Comp-Off Request</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Employee worked <strong>{compOff?.hoursWorked}h</strong> on{' '}
            <strong>{compOff?.workDate}</strong>. Set the credit days to grant:
          </Typography>
          <ToggleButtonGroup
            value={[0.5, 1.0].includes(creditDays) ? creditDays : null}
            exclusive
            onChange={(_, val) => { if (val !== null) setCreditDays(val); }}
            fullWidth
            sx={{ mb: 2 }}
          >
            <ToggleButton value={0.5} color="primary">0.5 day</ToggleButton>
            <ToggleButton value={1.0} color="primary">1.0 day</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            label="Custom Credit Days"
            type="number"
            fullWidth
            value={creditDays ?? ''}
            onChange={(e) => {
              const val = e.target.value === '' ? null : parseFloat(e.target.value);
              setCreditDays(val);
            }}
            inputProps={{ min: 0.5, max: 24, step: 0.5 }}
            InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
            helperText={`System calculated: ${compOff ? calculateCreditDays(compOff.hoursWorked) : '-'} day(s). Override if needed for special cases.`}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            color="success"
            onClick={handleApprove}
            loading={isSubmitting}
            disabled={!creditDays || creditDays <= 0}
          >
            Approve
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Comp-Off Request</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Please provide a reason for rejecting this comp-off request:</Typography>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Enter the reason for rejection..."
            required
            error={!rejectionReason.trim() && rejectionReason.length > 0}
            helperText={!rejectionReason.trim() && rejectionReason.length > 0 ? 'Rejection reason is required' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectDialogOpen(false);
              setRejectionReason('');
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            onClick={handleReject}
            loading={isSubmitting}
            disabled={!rejectionReason.trim()}
          >
            Reject
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Delete Comp-Off Request</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this comp-off request? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>No</Button>
          <LoadingButton variant="contained" color="error" onClick={handleCancel} loading={isSubmitting}>
            Yes, Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompOffDetails;
